﻿import { FC, useCallback, useId, useMemo, useRef } from 'react';
import { FormSkipReason } from '../../../../services/api/api-client';
import { useModal } from '../../../../application/hooks/useModal';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { Deferred } from '../../../../helpers/Deferred';
import { useTranslation } from 'react-i18next';
import { ValidationFormRules } from '../../../../helpers/validation-helpers';
import { AppRadioGroupField, AppTextField } from 'uikit/fields';
import styles from './useFormResultSkipping.module.scss';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';

export type SkipFormState = {
  skipReason: FormSkipReason | null;
  skipReasonDetails: string;
};

type FormResultSkippingResult = { $type: 'Saved' } | { $type: 'Canceled' };

export const useFormResultSkipping = (options: {
  title: string;
  subTitle?: string;
  text: string | JSX.Element;
  callback: (formState: SkipFormState) => Promise<string | undefined>;
}) => {
  const modal = useModal('CLOSED');
  const deferred = useRef<Deferred<FormResultSkippingResult>>();

  const form = useForm<SkipFormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const submit = form.handleSubmit(async (values) => {
    const result = await options.callback(values);
    if (result) {
      form.setError('root', { message: result });
      return;
    }

    modal.closeModal();
    deferred.current?.resolve({ $type: 'Saved' });
  });

  const cancel = useCallback(() => {
    modal.closeModal();
    deferred.current?.resolve({ $type: 'Canceled' });
  }, [modal]);

  return {
    start: () => {
      modal.openModal();
      form.reset({
        skipReason: null,
        skipReasonDetails: '',
      } satisfies SkipFormState);

      deferred.current = new Deferred();
      return deferred.current.promise;
    },
    modal: (
      <FormProvider {...form}>
        <SkipFormResultModal
          title={options.title}
          subTitle={options.subTitle}
          text={options.text}
          visible={modal.visible}
          onHide={cancel}
          onSave={submit}
        />
      </FormProvider>
    ),
  };
};

const SkipFormResultModal: FC<{
  visible: boolean;
  onHide: () => void;
  onSave: () => void;
  title: string;
  subTitle?: string;
  text?: string | JSX.Element;
}> = (props) => {
  const { t } = useTranslation();
  const formId = useId();
  const form = useFormContext<SkipFormState>();
  const error = form.formState.errors.root;

  return (
    <DialogModal
      testId={'skip-form-modal'}
      title={props.title}
      subTitle={props.subTitle}
      containerClassName={styles.modalContainer}
      bodyClassName={styles.modalBody}
      visible={props.visible}
      onHide={props.onHide}
      footer={{
        errors: error ? t('Forms.FormResultSkipping.Modal.Error') : undefined,
        leftButton: {
          text: t('Forms.FormResultSkipping.Modal.Cancel'),
          onClick: props.onHide,
          disabled: form.formState.isSubmitting,
        },
        rightButton: {
          text: t('Forms.FormResultSkipping.Modal.Save'),
          onClick: props.onSave,
          isLoading: form.formState.isSubmitting,
          disabled: form.formState.isSubmitting,
        },
      }}
    >
      <form id={`formResultSkipping-${formId}`}>
        <p className={styles.description}>{props.text}</p>
        <SkipReasonField />
        <DetailsField />
      </form>
    </DialogModal>
  );
};

const SkipReasonField: FC = () => {
  const { t } = useTranslation();
  const form = useFormContext<SkipFormState>();
  const { field, fieldState } = useController({
    control: form.control,
    name: 'skipReason',
    rules: {
      ...ValidationFormRules().requiredRule,
    },
  });

  const options = useMemo(
    () => [
      { value: FormSkipReason.Missed, text: t('Forms.FormSkipReason.Missed') },
      {
        value: FormSkipReason.NotApplicable,
        text: t('Forms.FormSkipReason.NotApplicable'),
      },
    ],
    [t],
  );

  return (
    <AppRadioGroupField
      labelProps={{ text: t('Forms.FormResultSkipping.Modal.Fields.Reason.Label'), isBold: true }}
      options={options}
      value={field.value ?? undefined}
      valueField={'value'}
      labelField={'text'}
      onChange={(x) => field.onChange(x?.value)}
      errorProps={{
        errors: fieldState.error?.message,
      }}
    />
  );
};

const DetailsField: FC = () => {
  const { t } = useTranslation();
  const form = useFormContext<SkipFormState>();
  const { field, fieldState } = useController({
    control: form.control,
    name: 'skipReasonDetails',
    rules: {
      ...ValidationFormRules().requiredRule,
    },
  });

  return (
    <AppTextField
      type="text-area"
      labelProps={{ text: t('Forms.FormResultSkipping.Modal.Fields.Details.Label') }}
      placeholder={t('Forms.FormResultSkipping.Modal.Fields.Details.Placeholder')}
      onChange={field.onChange}
      onBlur={field.onBlur}
      value={field.value}
      errorProps={{ errors: fieldState.error?.message }}
    />
  );
};
